<template>
  <v-container fluid v-if="show">
    <v-dialog v-model="show" max-width="800px" scrollable>
      <v-card>
        <v-card-title class="secondary white--text d-flex align-center justify-space-between">
          <span class="header-font" style="font-size: 22px;">
            {{ title }}
          </span>
        </v-card-title>
        <v-card-subtitle class="secondary py-2 white--text d-flex align-center justify-space-between">
          <span class="subheader-font body-2">{{ subtitle }}</span>
        </v-card-subtitle>
        <v-card-text class="py-0">
          <!--Clear / Select All-->
          <section class="d-flex justify-center mt-5">
            <v-btn small
              color="secondary"
              rounded outlined class="elevation-0 monospace-font px-4 me-2"
              @click="onClearClick()"
            >{{ $t('general.clear') }}</v-btn>
            <v-btn small color="secondary"
              rounded class="elevation-0 monospace-font white--text px-4"
              @click="onSelectAllClick()"
            >{{ $t('finishingMap.selectAll') }}</v-btn>
          </section>
          <!--Vertical Pitch-->
          <v-img v-if="isVertical"
            :src="require(`@/assets/img/half_pitch_${currentUserFieldColor}.png`)"
            contain class="my-5"
            :style="{position: 'relative', width: '100%', aspectRatio: '2 / 1'}"
            ref="pitchImg"
          >
            <div v-for="area in verticalAreas" :key="area.id">
              <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-on="on" v-bind="attrs"
                    :key="area.id"
                    :color="area.selected ? 'primary' : 'grey lighten-2'"
                    class="area-toggle"
                    :style="getButtonStyle(area)"
                    @click="toggleVerticalArea(area.id)"
                  ></v-btn>
                </template>
                <span class="black--text body-1">{{ area.label }}</span>
              </v-tooltip>
            </div>
          </v-img>
          <!--Horizontal Pitch-->
          <v-img v-if="isHorizontal || (!isVertical && !isFullWidth && !isGoal)"
            :src="require(`@/assets/img/pitch_${currentUserFieldColor}.png`)"
            contain class="my-5"
            :style="{position: 'relative', width: '100%', aspectRatio: '2 / 1'}"
            ref="pitchImg"
          >
            <div v-for="area in horizontalAreas" :key="area.id">
              <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-on="on" v-bind="attrs"
                    :key="area.id"
                    :color="area.selected ? 'primary' : 'grey lighten-2'"
                    class="area-toggle"
                    :style="getButtonStyle(area)"
                    @click="toggleHorizontalArea(area.parentId)"
                  ></v-btn>
                </template>
                <span class="black--text body-1">{{ area.label }}</span>
              </v-tooltip>
            </div>
          </v-img>
          <!--Full Width Pitch-->
          <v-img v-if="isFullWidth"
            :src="require(`@/assets/img/pitch_${currentUserFieldColor}.png`)"
            contain class="my-5"
            :style="{position: 'relative', width: '100%', aspectRatio: '2 / 1'}"
            ref="pitchImg"
          >
            <div v-for="area in fullWidthAreas" :key="area.id">
              <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-on="on" v-bind="attrs"
                    :key="area.id"
                    :color="area.selected ? 'primary' : 'grey lighten-2'"
                    class="area-toggle"
                    :style="getButtonStyle(area)"
                    @click="toggleFullWidthArea(area.id)"
                  ></v-btn>
                </template>
                <span class="black--text body-1">{{ area.label }}</span>
              </v-tooltip>
            </div>
          </v-img>
          <!--Goal Pitch-->
          <v-img v-if="isGoal"
            :src="require(`@/assets/img/front_goal.png`)"
            contain class="my-5"
            :style="{position: 'relative', width: '100%', aspectRatio: '2 / 1'}"
            ref="pitchImg"
          >
            <div v-for="area in goalAreas" :key="area.id">
              <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-on="on" v-bind="attrs"
                    :key="area.id"
                    :color="area.selected ? 'primary' : 'grey lighten-2'"
                    class="area-toggle"
                    :style="getButtonStyle(area)"
                    @click="toggleGoalArea(area.id)"
                  ></v-btn>
                </template>
                <span class="black--text body-1">{{ area.label }}</span>
              </v-tooltip>
            </div>
          </v-img>
          <!--Selected Areas-->
          <section class="d-flex align-center justify-start flex-wrap" v-if="selectedAreas.length">
            <span class="body-1 font-weight-bold primary--text ms-2">Selected areas: </span>
            <v-chip v-for="(area, i) in selectedAreas" :key="i" small color="lightSecondary lighten-2" class="body-1 ma-1 black--text">
              <span class="font-weight-medium">{{ area.label }}</span>
            </v-chip>
          </section>
        </v-card-text>
        <v-card-actions class="pr-6 pb-4 d-flex justify-end">
          <v-btn
            :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'"
            rounded outlined class="elevation-0 monospace-font px-4"
            @click="onCancelClick()"
          >{{$t('general.cancel')}}</v-btn>
          <v-btn
            rounded class="elevation-0 monospace-font white--text px-4" color="primary"
            @click="onConfirmClick()"
          >{{ $t('general.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

export default {
  name: 'AreaSelectorModal',
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    isVertical: {
      type: Boolean,
      default: false
    },
    isHorizontal: {
      type: Boolean,
      default: false
    },
    isFullWidth: {
      type: Boolean,
      default: false
    },
    isGoal: {
      type: Boolean,
      default: false
    },
    areaModel: {
      type: Array,
      default: () => [],
      required: true
    }
  },
  data() {
    return {
      show: false,
      areas: [],
      horizontalAreas: [],
      verticalAreas: [
        { id: 1, code: 'goalArea', label: this.$t("finishingMap.goalArea"), xPercent: 50, yPercent: 13, widthPercent: 14, heightPercent: 7, selected: false },
        { id: 2, code: 'boxRight', label: this.$t("finishingMap.boxRight"), xPercent: 62.1, yPercent: 22.5, widthPercent: 7.75, heightPercent: 24.5, selected: false },
        { id: 3, code: 'boxCenter', label: this.$t("finishingMap.boxCenter"), xPercent: 50.13, yPercent: 26.3, widthPercent: 15, heightPercent: 16.25, selected: false },
        { id: 4, code: 'boxLeft', label: this.$t("finishingMap.boxLeft"), xPercent: 38.1, yPercent: 22.5, widthPercent: 7.75, heightPercent: 24.5, selected: false },
        { id: 5, code: 'outBoxDeepRight', label: this.$t("finishingMap.outBoxDeepRight"), xPercent: 72.3, yPercent: 23, widthPercent: 9.75, heightPercent: 25, selected: false },
        { id: 6, code: 'outBoxDeepLeft', label: this.$t("finishingMap.outBoxDeepLeft"), xPercent: 27.9, yPercent: 23, widthPercent: 9.75, heightPercent: 25, selected: false },
        { id: 7, code: 'outBoxRight', label: this.$t("finishingMap.outBoxRight"), xPercent: 72.3, yPercent: 51, widthPercent: 9.75, heightPercent: 28.75, selected: false },
        { id: 8, code: 'outBoxCentreRight', label: this.$t("finishingMap.outBoxCentreRight"), xPercent: 62.5, yPercent: 51, widthPercent: 8.5, heightPercent: 28.75, selected: false },
        { id: 9, code: 'outBoxCentre', label: this.$t("finishingMap.outBoxCentre"), xPercent: 50.1, yPercent: 51, widthPercent: 15.125, heightPercent: 28.75, selected: false },
        { id: 10, code: 'outBoxCentreLeft', label: this.$t("finishingMap.outBoxCentreLeft"), xPercent: 37.67, yPercent: 51, widthPercent: 8.5, heightPercent: 28.75, selected: false },
        { id: 11, code: 'outBoxLeft', label: this.$t("finishingMap.outBoxLeft"), xPercent: 27.9, yPercent: 51, widthPercent: 9.75, heightPercent: 28.75, selected: false },
        { id: 12, code: 'right35', label: this.$t("finishingMap.right35"), xPercent: 72.3, yPercent: 79, widthPercent: 9.75, heightPercent: 25, selected: false },
        { id: 13, code: 'centreRight35', label: this.$t("finishingMap.centreRight35"), xPercent: 62.5, yPercent: 79, widthPercent: 8.5, heightPercent: 25, selected: false },
        { id: 14, code: 'centre35', label: this.$t("finishingMap.centre35"), xPercent: 50.1, yPercent: 79, widthPercent: 15.125, heightPercent: 25, selected: false },
        { id: 15, code: 'centreLeft35', label: this.$t("finishingMap.centreLeft35"), xPercent: 37.67, yPercent: 79, widthPercent: 8.5, heightPercent: 25, selected: false },
        { id: 16, code: 'left35', label: this.$t("finishingMap.left35"), xPercent: 27.9, yPercent: 79, widthPercent: 9.75, heightPercent: 25, selected: false },
      ],
      firstHalfAreas: [
        { id: 1, code: 'goalArea', parentId: 1, code: 'goalArea', label: this.$t("finishingMap.goalArea"), xPercent: 20.7, yPercent: 50, widthPercent: 3, heightPercent: 20, selected: false },
        { id: 2, code: 'boxRight', parentId: 2, label: this.$t("finishingMap.boxRight"), xPercent: 24, yPercent: 32.4, widthPercent: 9.5, heightPercent: 10.8, selected: false },
        { id: 3, code: 'boxCenter', parentId: 3, label: this.$t("finishingMap.boxCenter"), xPercent: 25.8, yPercent: 50, widthPercent: 5.7, heightPercent: 22.5, selected: false },
        { id: 4, code: 'boxLeft', parentId: 4, label: this.$t("finishingMap.boxLeft"), xPercent: 24, yPercent: 67.5, widthPercent: 9.5, heightPercent: 10.8, selected: false },
        { id: 5, code: 'outBoxDeepRight', parentId: 5, label: this.$t("finishingMap.outBoxDeepRight"), xPercent: 24.2, yPercent: 17.3, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 6, code: 'outBoxDeepLeft', parentId: 6, label: this.$t("finishingMap.outBoxDeepLeft"), xPercent: 24.2, yPercent: 82.5, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 7, code: 'outBoxRight', parentId: 7, label: this.$t("finishingMap.outBoxRight"), xPercent: 34.6, yPercent: 17.3, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 8, code: 'outBoxCentreRight', parentId: 8, label: this.$t("finishingMap.outBoxCentreRight"), xPercent: 34.6, yPercent: 31.8, widthPercent: 10, heightPercent: 11.7, selected: false },
        { id: 9, code: 'outBoxCentre', parentId: 9, label: this.$t("finishingMap.outBoxCentre"), xPercent: 34.6, yPercent: 50, widthPercent: 10, heightPercent: 22.5, selected: false },
        { id: 10, code: 'outBoxCentreLeft', parentId: 10, label: this.$t("finishingMap.outBoxCentreLeft"), xPercent: 34.6, yPercent: 68, widthPercent: 10, heightPercent: 11.7, selected: false },
        { id: 11, code: 'outBoxLeft', parentId: 11, label: this.$t("finishingMap.outBoxLeft"), xPercent: 34.6, yPercent: 82.5, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 12, code: 'right35', parentId: 12, label: this.$t("finishingMap.right35"), xPercent: 44.7, yPercent: 17.3, widthPercent: 9.5, heightPercent: 15.5, selected: false },
        { id: 13, code: 'centreRight35', parentId: 13, label: this.$t("finishingMap.centreRight35"), xPercent: 44.7, yPercent: 31.8, widthPercent: 9.5, heightPercent: 11.7, selected: false },
        { id: 14, code: 'centre35', parentId: 14, label: this.$t("finishingMap.centre35"), xPercent: 44.7, yPercent: 50, widthPercent: 9.5, heightPercent: 22.5, selected: false },
        { id: 15, code: 'centreLeft35', parentId: 15, label: this.$t("finishingMap.centreLeft35"), xPercent: 44.7, yPercent: 68, widthPercent: 9.5, heightPercent: 11.7, selected: false },
        { id: 16, code: 'left35', parentId: 16, label: this.$t("finishingMap.left35"), xPercent: 44.7, yPercent: 82.5, widthPercent: 9.5, heightPercent: 15.5, selected: false },
      ],
      fullWidthAreas: [
        { id: 0, label: this.$t("videoHighlights.areas.goalArea"), code: 'goalArea', xPercent: 79.3, yPercent: 50, widthPercent: 3, heightPercent: 20, selected: false },
        { id: 1, label: this.$t("videoHighlights.areas.boxLeft"), code: 'boxLeft', xPercent: 76, yPercent: 32.4, widthPercent: 9.5, heightPercent: 10.8, selected: false },
        { id: 2, label: this.$t("videoHighlights.areas.boxCenter"), code: 'boxCenter', xPercent: 74.15, yPercent: 50, widthPercent: 5.7, heightPercent: 22.5, selected: false },
        { id: 3, label: this.$t("videoHighlights.areas.boxRight"), code: 'boxRight', xPercent: 76, yPercent: 67.5, widthPercent: 9.5, heightPercent: 10.8, selected: false },
        { id: 4, label: this.$t("videoHighlights.areas.outBoxDeepLeft"), code: 'outBoxDeepLeft', xPercent: 75.8, yPercent: 17.3, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 5, label: this.$t("videoHighlights.areas.outBoxDeepRight"), code: 'outBoxDeepRight', xPercent: 75.8, yPercent: 82.5, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 6, label: this.$t("videoHighlights.areas.outBoxLeft"), code: 'outBoxLeft', xPercent: 65.4, yPercent: 17.3, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 7, label: this.$t("videoHighlights.areas.outBoxCentreLeft"), code: 'outBoxCentreLeft', xPercent: 65.4, yPercent: 31.8, widthPercent: 10, heightPercent: 11.7, selected: false },
        { id: 8, label: this.$t("videoHighlights.areas.outBoxCentre"), code: 'outBoxCentre', xPercent: 65.4, yPercent: 50, widthPercent: 10, heightPercent: 22.5, selected: false },
        { id: 9, label: this.$t("videoHighlights.areas.outBoxCentreRight"), code: 'outBoxCentreRight', xPercent: 65.4, yPercent: 68, widthPercent: 10, heightPercent: 11.7, selected: false },
        { id: 10, label: this.$t("videoHighlights.areas.outBoxRight"), code: 'outBoxRight', xPercent: 65.4, yPercent: 82.5, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 11, label: this.$t("videoHighlights.areas.midfieldLeft35"), code: 'midfieldLeft35', xPercent: 50, yPercent: 17.3, widthPercent: 20, heightPercent: 15.5, selected: false },
        { id: 12, label: this.$t("videoHighlights.areas.midfieldCentreLeft35"), code: 'midfieldCentreLeft35', xPercent: 50, yPercent: 31.8, widthPercent: 20, heightPercent: 11.7, selected: false },
        { id: 13, label: this.$t("videoHighlights.areas.midfieldCentre35"), code: 'midfieldCentre35', xPercent: 50, yPercent: 50, widthPercent: 20, heightPercent: 22.5, selected: false },
        { id: 14, label: this.$t("videoHighlights.areas.midfieldCentreRight35"), code: 'midfieldCentreRight35',xPercent: 50, yPercent: 68, widthPercent: 20, heightPercent: 11.7, selected: false },
        { id: 15, label: this.$t("videoHighlights.areas.midfieldRight35"), code: 'midfieldRight35', xPercent: 50, yPercent: 82.5, widthPercent: 20, heightPercent: 15.5, selected: false },
        { id: 16, label: this.$t("videoHighlights.areas.ownGoalArea"), code: 'ownGoalArea', xPercent: 20.7, yPercent: 50, widthPercent: 3, heightPercent: 20, selected: false },
        { id: 17, label: this.$t("videoHighlights.areas.ownBoxLeft"), code: 'ownBoxLeft', xPercent: 24, yPercent: 32.4, widthPercent: 9.5, heightPercent: 10.8, selected: false },
        { id: 18, label: this.$t("videoHighlights.areas.ownBoxCenter"), code: 'ownBoxCenter', xPercent: 25.8, yPercent: 50, widthPercent: 5.7, heightPercent: 22.5, selected: false },
        { id: 19, label: this.$t("videoHighlights.areas.ownBoxRight"), code: 'ownBoxRight', xPercent: 24, yPercent: 67.5, widthPercent: 9.5, heightPercent: 10.8, selected: false },
        { id: 20, label: this.$t("videoHighlights.areas.ownFieldOutOfBoxDeepLeft"), code: 'ownFieldOutOfBoxDeepLeft', xPercent: 24.2, yPercent: 17.3, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 21, label: this.$t("videoHighlights.areas.ownFieldOutOfBoxDeepRight"), code: 'ownFieldOutOfBoxDeepRight', xPercent: 24.2, yPercent: 82.5, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 22, label: this.$t("videoHighlights.areas.ownFieldOutOfBoxLeft"), code: 'ownFieldOutOfBoxLeft', xPercent: 34.6, yPercent: 17.3, widthPercent: 10, heightPercent: 15.5, selected: false },
        { id: 23, label: this.$t("videoHighlights.areas.ownFieldOutOfBoxCentreLeft"), code: 'ownFieldOutOfBoxCentreLeft', xPercent: 34.6, yPercent: 31.8, widthPercent: 10, heightPercent: 11.7, selected: false },
        { id: 24, label: this.$t("videoHighlights.areas.ownFieldOutOfBoxCentre"), code: 'ownFieldOutOfBoxCentre', xPercent: 34.6, yPercent: 50, widthPercent: 10, heightPercent: 22.5, selected: false },
        { id: 25, label: this.$t("videoHighlights.areas.ownFieldOutOfBoxCentreRight"), code: 'ownFieldOutOfBoxCentreRight', xPercent: 34.6, yPercent: 68, widthPercent: 10, heightPercent: 11.7, selected: false },
        { id: 26, label: this.$t("videoHighlights.areas.ownFieldOutOfBoxRight"), code: 'ownFieldOutOfBoxRight', xPercent: 34.6, yPercent: 82.5, widthPercent: 10, heightPercent: 15.5, selected: false }
      ],
      goalAreas: [
        { id: 1, code: 'highLeft', label: this.$t("finishingMap.highLeft"), xPercent: 35, yPercent: 49.5, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 2, code: 'highCenter', label: this.$t("finishingMap.highCenter"), xPercent: 50.16, yPercent: 49.5, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 3, code: 'highRight', label: this.$t("finishingMap.highRight"), xPercent: 65.3, yPercent: 49.5, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 4, code: 'lowLeft', label: this.$t("finishingMap.lowLeft"), xPercent: 35, yPercent: 65, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 5, code: 'lowCenter', label: this.$t("finishingMap.lowCenter"), xPercent: 50.16, yPercent: 65, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 6, code: 'lowRight', label: this.$t("finishingMap.lowRight"), xPercent: 65.3, yPercent: 65, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 7, code: 'outHighLeft', label: this.$t("finishingMap.outHighLeft"), xPercent: 14.25, yPercent: 10.5, widthPercent: 23.5, heightPercent: 18.5, selected: false  },
        { id: 8, code: 'high', label: this.$t("finishingMap.high"), xPercent: 50, yPercent: 10.5, widthPercent: 47, heightPercent: 18.5, selected: false  },
        { id: 9, code: 'outHighRight', label: this.$t("finishingMap.outHighRight"), xPercent: 86, yPercent: 10.5, widthPercent: 23.5, heightPercent: 18.5, selected: false  },
        { id: 10, code: 'closeHighLeft', label: this.$t("finishingMap.closeHighLeft"), xPercent: 14.25, yPercent: 30, widthPercent: 23.5, heightPercent: 18.5, selected: false  },
        { id: 11, code: 'closeHigh', label: this.$t("finishingMap.closeHigh"), xPercent: 50, yPercent: 30, widthPercent: 47, heightPercent: 18.5, selected: false  },
        { id: 12, code: 'closeHighRight', label: this.$t("finishingMap.closeHighRight"), xPercent: 86, yPercent: 30, widthPercent: 23.5, heightPercent: 18.5, selected: false  },
        { id: 13, code: 'closeLeft', label: this.$t("finishingMap.closeLeft"), xPercent: 14.25, yPercent: 56.5, widthPercent: 23.5, heightPercent: 31.5, selected: false  },
        { id: 14, code: 'closeRight', label: this.$t("finishingMap.closeRight"), xPercent: 86, yPercent: 56.5, widthPercent: 23.5, heightPercent: 31.5, selected: false  },
      ],
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserFieldColor() {
      return this.$store.getters["auth/fieldColor"];
    },
    selectedAreas() {
      return this.areas.filter(a => a.selected && a.id < 100);
    }
  },
  created() {},
  mounted() {},
  methods: {
    async open() {
      //Set areas based on pitch type
      if (this.isVertical) {
        this.areas = this.verticalAreas;
      } else if (this.isFullWidth) {
        this.areas = this.fullWidthAreas;
      } else if (this.isGoal) { 
        this.areas = this.goalAreas;
      } else {
        const secondHalfAreas = this.firstHalfAreas.map(area => ({
          ...area,
          id: area.id + 100,
          xPercent: 100 - area.xPercent,
          yPercent: 100 - area.yPercent,
        }));
        this.horizontalAreas = [...this.firstHalfAreas, ...secondHalfAreas];
        this.areas = this.horizontalAreas;
      };
      //Set selected areas
      if (this.areaModel.length) {
        this.areas.forEach(area => {
          if (this.areaModel.includes(area.id) || this.areaModel.includes(area.code) || this.areaModel.includes(area.parentId)) {
            area.selected = true;
          };
        });
      };
      this.show = true;
    },
    close() {
      this.areas = [];
      this.show = false;
    },
    onCancelClick() {
      this.close();
    },
    toggleVerticalArea(areaId) {
      const area = this.verticalAreas.find(a => a.id === areaId);
      if (area) area.selected = !area.selected;
    },
    toggleHorizontalArea(areaId) {
      const areas = this.horizontalAreas.filter(a => a.parentId === areaId);
      if (areas.length) areas.forEach(area => area.selected = !area.selected);
    },
    toggleFullWidthArea(areaId) {
      const area = this.fullWidthAreas.find(a => a.id === areaId);
      if (area) area.selected = !area.selected;
    },
    toggleGoalArea(areaId) {
      const area = this.goalAreas.find(a => a.id === areaId);
      if (area) area.selected = !area.selected;
    },
    getButtonStyle(area) {
      const containerWidth = window.innerWidth;
      let scaleFactor = 1;

      if (containerWidth < 850) scaleFactor = 0.6;
      else if (containerWidth < 1200) scaleFactor = 0.8;

      const width = area.widthPercent * scaleFactor;
      const height = area.heightPercent * scaleFactor;

      return {
        position: 'absolute',
        left: `${area.xPercent}%`,
        top: `${area.yPercent}%`,
        transform: 'translate(-50%, -50%)',
        width: `${width}%`,
        height: `${height}%`,
        minWidth: '10px',
        minHeight: '10px',
        opacity: 0.5,
      };
    },
    onConfirmClick() {
      const selectedAreasObj = { ids: this.selectedAreas.map(a => a.id), codes: this.selectedAreas.map(a => a.code) };
      this.$emit("onConfirmClick", selectedAreasObj);
      this.close();
    },
    onClearClick() {
      this.areas.forEach(area => area.selected = false);
    },
    onSelectAllClick() {
      this.areas.forEach(area => area.selected = true);
    }
  },
};
</script>

<style scoped>
  .area-toggle.v-btn {
    padding: 0;
    min-width: 10px !important;
    min-height: 10px !important;
  }
</style>



// WEBPACK FOOTER //
// src/views/modals/AreaSelectorModal.vue